import PropTypes from 'prop-types';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton } from '@ui/button';
import { Input } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules/ResponsiveImage';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { StyledFormWithImage } from '../StyledForm';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { Question } from '../Question';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import {
  FieldWrapper,
  ImageContainer,
  ArrowContainer,
  MobileImageContainer,
  DesktopImageContainer,
  ButtonContainer,
  Subheadline,
} from './styles.js';
import { schema } from './schema';

export { schema } from './schema';

export const PupNameForm = ({
  defaultValues,
  goBack,
  labelName,
  subheadline,
  index,
  onSubmit,
  pupNameQuestionCaption,
  pupNameQuestionPrompt,
  pupNameCallout,
  ctaButtonText = 'Continue',
  mobileImage,
  desktopImage,
  flowType,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const isInSubmittingState =
    isSubmitting || (isSubmitted && isSubmitSuccessful);

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [hasFocused, setHasFocused] = useState(false);

  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCartContext();

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('pupname_step', cart?.customer || customer),
    );
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(
        index,
        'dog_name',
        cart?.customer || customer,
        data,
        flowType,
      ),
    );
    const result = await onSubmit(data);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };
  return (
    <StyledFormWithImage
      data-testid="onboarding-dog-name"
      onSubmit={handleSubmit(submitForm)}
    >
      <ImageContainer>
        <ArrowContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            data-testid="onboarding-dog-name-back"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isInSubmittingState}
            type="button"
            variant="primary"
          />
        </ArrowContainer>
        <MobileImageContainer>
          <ResponsiveImage priority={true} {...mobileImage} width={400} />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <FormWrapper
        $minHeight={
          isInputFocused && pupNameCallout && !hasFocused ? '350' : '253'
        }
      >
        <FormFieldsContainer $maxWidth={391}>
          <Question
            caption={pupNameQuestionCaption}
            prompt={pupNameQuestionPrompt}
            callout={pupNameCallout}
            calloutMargin={[3, 0, 1]}
            hideCaptionOrCallout={isInputFocused || hasFocused}
            typography={'heading4'}
          >
            {subheadline && <Subheadline>{subheadline}</Subheadline>}
            <FieldWrapper>
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    visuallyHideLabel
                    labelText={labelName}
                    error={fieldState.error?.message}
                    status={fieldState.error ? 'error' : null}
                    value={field.value || ''}
                    borderColor={'text.tertiary'}
                    centeredAlign={true}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => {
                      setTimeout(() => {
                        setIsInputFocused(false);
                        setHasFocused(true);
                      }, 200);
                    }}
                  />
                )}
              />
            </FieldWrapper>
          </Question>
          <ButtonContainer>
            <Button
              type="submit"
              aria-label={ctaButtonText}
              data-testid="onboarding-dog-name-continue"
              disabled={isInSubmittingState}
              variant="primary"
              isLoading={isInSubmittingState}
              horizontalPadding={32}
            >
              {ctaButtonText}
            </Button>
          </ButtonContainer>
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithImage>
  );
};

PupNameForm.displayName = 'PupNameForm';

PupNameForm.propTypes = {
  defaultValues: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  labelName: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  subheadline: PropTypes.string,
  pupNameQuestionCaption: PropTypes.string.isRequired,
  pupNameQuestionPrompt: PropTypes.string.isRequired,
  pupNameCallout: PropTypes.object,
  ctaButtonText: PropTypes.string,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  flowType: PropTypes.string,
};
